import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Loader2, AlertCircle, Database, X, User, Calendar, Filter, FileText, ThumbsUp, Clock, Flame } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import GuestGate from "@/components/GuestGate";
import ProGate from "@/components/ProGate";

export default function RealPrograms() {
  const { t } = useLang();
  const { user } = useAuth();
  const isGuest = !user;
  const isFree = user && !user.is_subscribed && user.role !== "admin";

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Filters
  const [fMaterial, setFMaterial] = useState("");
  const [fHead, setFHead] = useState("");
  const [fDnMin, setFDnMin] = useState("");
  const [fDnMax, setFDnMax] = useState("");
  const [sort, setSort] = useState("new"); // new | top

  const canAccess = user && (user.is_subscribed || user.role === "admin");

  const fetchPrograms = useCallback(async () => {
    if (!canAccess) { setLoading(false); return; }
    setLoading(true); setError("");
    try {
      const params = { sort };
      if (fMaterial.trim()) params.material = fMaterial.trim();
      if (fHead.trim()) params.head_type = fHead.trim();
      if (fDnMin) params.dn_min = parseFloat(fDnMin);
      if (fDnMax) params.dn_max = parseFloat(fDnMax);
      const r = await api.get("/programs/real", { params });
      setPrograms(r.data.programs || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load programs");
    } finally { setLoading(false); }
  }, [canAccess, fMaterial, fHead, fDnMin, fDnMax, sort]);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms]);

  const clearFilters = () => { setFMaterial(""); setFHead(""); setFDnMin(""); setFDnMax(""); };

  const onDelete = async (id) => {
    if (!window.confirm(t.realprog.delConfirm)) return;
    try {
      await api.delete(`/programs/real/${id}`);
      setPrograms((ps) => ps.filter((p) => p.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  const onUpvote = async (id) => {
    // Optimistic UI: toggle locally first, rollback on error
    setPrograms((ps) => ps.map((p) => p.id === id ? {
      ...p,
      voted_by_me: !p.voted_by_me,
      upvote_count: Math.max(0, (p.upvote_count || 0) + (p.voted_by_me ? -1 : 1)),
    } : p));
    try {
      const r = await api.post(`/programs/real/${id}/upvote`);
      setPrograms((ps) => ps.map((p) => p.id === id ? {
        ...p, voted_by_me: r.data.voted_by_me, upvote_count: r.data.upvote_count,
      } : p));
    } catch (e) {
      // rollback
      setPrograms((ps) => ps.map((p) => p.id === id ? {
        ...p,
        voted_by_me: !p.voted_by_me,
        upvote_count: Math.max(0, (p.upvote_count || 0) + (p.voted_by_me ? -1 : 1)),
      } : p));
    }
  };

  return (
    <div data-testid="real-programs-page" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="wb-tag !border-gold-600 !text-gold-500">
            <Database className="w-3 h-3 inline mr-1" /> REAL PROGRAMS
          </span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">{t.realprog.title}</h1>
          <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider mt-2">{t.realprog.sub}</p>
        </div>
        {canAccess && !showForm && (
          <button
            data-testid="real-prog-add-btn"
            onClick={() => setShowForm(true)}
            className="wb-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm self-start"
          >
            <Plus className="w-4 h-4" /> {t.realprog.addBtn}
          </button>
        )}
      </div>

      {isGuest && (
        <div className="max-w-2xl">
          <GuestGate customMsg={t.realprog.guestMsg} />
        </div>
      )}

      {isFree && (
        <div className="max-w-2xl">
          <ProGate message={t.realprog.paywallMsg} />
        </div>
      )}

      {canAccess && showForm && (
        <SubmitForm
          onClose={() => setShowForm(false)}
          onCreated={(p) => { setPrograms((ps) => [p, ...ps]); setShowForm(false); }}
        />
      )}

      {canAccess && (
        <>
          {/* Filters + sort */}
          <div className="mb-6 border border-zinc-800 bg-[var(--wb-surface)] p-4" data-testid="real-prog-filters">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-gold-500" />
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">{t.realprog.filterTitle}</span>
              </div>
              <div className="inline-flex border border-zinc-800" data-testid="real-prog-sort">
                <button data-testid="sort-new" onClick={() => setSort("new")}
                  className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${sort === "new" ? "bg-gold-500/15 text-gold-300" : "text-zinc-500 hover:text-zinc-200"}`}>
                  <Clock className="w-3 h-3" /> {t.realprog.sortNew}
                </button>
                <button data-testid="sort-top" onClick={() => setSort("top")}
                  className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${sort === "top" ? "bg-gold-500/15 text-gold-300" : "text-zinc-500 hover:text-zinc-200"}`}>
                  <Flame className="w-3 h-3" /> {t.realprog.sortTop}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <input data-testid="filter-material" type="text" value={fMaterial} onChange={(e) => setFMaterial(e.target.value)}
                placeholder={t.realprog.filterMaterialPh} className="wb-input px-2.5 py-1.5 text-xs" />
              <input data-testid="filter-head" type="text" value={fHead} onChange={(e) => setFHead(e.target.value)}
                placeholder={t.realprog.filterHeadPh} className="wb-input px-2.5 py-1.5 text-xs" />
              <input data-testid="filter-dn-min" type="number" min="0" step="0.1" value={fDnMin} onChange={(e) => setFDnMin(e.target.value)}
                placeholder={t.realprog.filterDnMin} className="wb-input px-2.5 py-1.5 text-xs font-mono" />
              <input data-testid="filter-dn-max" type="number" min="0" step="0.1" value={fDnMax} onChange={(e) => setFDnMax(e.target.value)}
                placeholder={t.realprog.filterDnMax} className="wb-input px-2.5 py-1.5 text-xs font-mono" />
              <button data-testid="filter-clear" onClick={clearFilters}
                className="wb-btn-ghost px-3 py-1.5 text-xs">{t.realprog.clearFilters}</button>
            </div>
            <div className="mt-2 text-[11px] text-zinc-500 font-mono">
              {programs.length} {t.realprog.countLabel}
            </div>
          </div>

          {error && (
            <div className="border border-red-900 bg-red-950/30 text-red-400 text-sm p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          {loading ? (
            <div className="border border-zinc-800 border-dashed p-12 text-center text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t.common.loading}
            </div>
          ) : programs.length === 0 ? (
            <div data-testid="real-prog-empty" className="border border-zinc-800 border-dashed p-12 text-center">
              <Database className="w-10 h-10 mx-auto mb-3 text-zinc-700" strokeWidth={1.2} />
              <p className="text-sm text-zinc-500">{t.realprog.empty}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="real-prog-grid">
              {programs.map((p) => (
                <ProgramCard key={p.id} program={p} currentUser={user} onDelete={onDelete} onUpvote={onUpvote} t={t} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgramCard({ program: p, currentUser, onDelete, onUpvote, t }) {
  const canDelete = currentUser && (currentUser.id === p.user_id || currentUser.role === "admin");
  const isMine = currentUser && currentUser.id === p.user_id;
  const voted = !!p.voted_by_me;
  const count = p.upvote_count || 0;
  return (
    <div data-testid={`real-prog-card-${p.id}`} className="border border-zinc-800 bg-[var(--wb-surface)] hover:border-gold-700/40 transition-colors flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-gold-400 mb-0.5">{p.machine}</div>
            <div className="font-display font-bold text-base text-white truncate">{p.material}</div>
          </div>
          {canDelete && (
            <button data-testid={`real-prog-del-${p.id}`} onClick={() => onDelete(p.id)}
              title={t.realprog.del}
              className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
          <div>
            <div className="text-zinc-600 uppercase">{t.realprog.headLbl.split("(")[0].trim()}</div>
            <div className="text-zinc-300 truncate">{p.head_type || "—"}</div>
          </div>
          <div>
            <div className="text-zinc-600 uppercase">{t.realprog.jointLbl}</div>
            <div className="text-zinc-300 truncate">{p.joint_type || "—"}</div>
          </div>
          <div>
            <div className="text-zinc-600 uppercase">DN</div>
            <div className="text-gold-400 font-bold">⌀{p.diameter_mm} mm</div>
          </div>
          <div>
            <div className="text-zinc-600 uppercase">{t.realprog.wallLbl.split("(")[0].trim()}</div>
            <div className="text-gold-400 font-bold">{p.wall_thickness_mm} mm</div>
          </div>
        </div>
      </div>

      {/* Parameters block */}
      <div className="p-4 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1">
          <FileText className="w-3 h-3" /> {t.realprog.paramsLbl.split("(")[0].trim()}
        </div>
        <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-words bg-black/30 border border-zinc-800 p-2.5 max-h-48 overflow-y-auto leading-relaxed">
{p.parameters}
        </pre>
        {p.notes && (
          <div className="mt-2.5 text-[11px] text-zinc-400 italic border-l-2 border-gold-700/40 pl-2.5 leading-relaxed">
            {p.notes}
          </div>
        )}
      </div>

      {/* Author footer */}
      <div className="px-4 py-2.5 border-t border-zinc-800 text-[10px] font-mono text-zinc-500 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {p.user_picture ? (
            <img src={p.user_picture} alt="" className="w-4 h-4 rounded-full object-cover" />
          ) : (
            <User className="w-3 h-3" />
          )}
          <span className="truncate text-zinc-400">{p.user_name}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1 text-zinc-600">
            <Calendar className="w-3 h-3" />
            {new Date(p.created_at).toLocaleDateString()}
          </div>
          <button
            data-testid={`real-prog-upvote-${p.id}`}
            onClick={() => !isMine && onUpvote(p.id)}
            disabled={isMine}
            title={isMine ? "" : (voted ? t.realprog.upvotedTip : t.realprog.upvoteTip)}
            className={`inline-flex items-center gap-1 px-2 py-1 border transition-colors text-[11px] font-mono ${
              isMine
                ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
                : voted
                  ? "border-gold-600 bg-gold-500/15 text-gold-300 hover:bg-gold-500/25"
                  : "border-zinc-800 text-zinc-400 hover:border-gold-700 hover:text-gold-400"
            }`}
          >
            <ThumbsUp className={`w-3 h-3 ${voted ? "fill-current" : ""}`} />
            <span data-testid={`real-prog-upvote-count-${p.id}`}>{count}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmitForm({ onClose, onCreated }) {
  const { t } = useLang();
  const [material, setMaterial] = useState("");
  const [machine, setMachine] = useState("");
  const [headType, setHeadType] = useState("");
  const [jointType, setJointType] = useState("pipePipe");
  const [diameter, setDiameter] = useState("");
  const [wall, setWall] = useState("");
  const [parameters, setParameters] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const jointKeys = ["pipePipe", "pipeElbow", "pipeT", "pipeReduction", "other"];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!material.trim() || !machine.trim() || !parameters.trim() || !diameter || !wall) {
      setErr(t.realprog.requiredErr); return;
    }
    setSaving(true);
    try {
      const r = await api.post("/programs/real", {
        material: material.trim(),
        machine: machine.trim(),
        head_type: headType.trim(),
        joint_type: t.realprog.jointTypes[jointType],
        diameter_mm: parseFloat(diameter),
        wall_thickness_mm: parseFloat(wall),
        parameters: parameters.trim(),
        notes: notes.trim(),
      });
      onCreated(r.data.program);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} data-testid="real-prog-form" className="mb-6 border border-gold-700/50 bg-gradient-to-br from-zinc-900 to-black p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Plus className="w-4 h-4 text-gold-500" /> {t.realprog.formTitle}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">{t.realprog.formSub}</p>
        </div>
        <button type="button" onClick={onClose} data-testid="real-prog-form-close"
          className="p-1.5 text-zinc-500 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t.realprog.materialLbl} required>
          <input data-testid="rp-material" type="text" value={material} onChange={(e) => setMaterial(e.target.value)} required
            className="wb-input w-full px-3 py-2 text-sm" />
        </Field>
        <Field label={t.realprog.machineLbl} required>
          <input data-testid="rp-machine" type="text" value={machine} onChange={(e) => setMachine(e.target.value)} required
            className="wb-input w-full px-3 py-2 text-sm" />
        </Field>
        <Field label={t.realprog.headLbl}>
          <input data-testid="rp-head" type="text" value={headType} onChange={(e) => setHeadType(e.target.value)}
            className="wb-input w-full px-3 py-2 text-sm" />
        </Field>
        <Field label={t.realprog.jointLbl}>
          <select data-testid="rp-joint" value={jointType} onChange={(e) => setJointType(e.target.value)}
            className="wb-input w-full px-3 py-2 text-sm">
            {jointKeys.map((k) => (
              <option key={k} value={k}>{t.realprog.jointTypes[k]}</option>
            ))}
          </select>
        </Field>
        <Field label={t.realprog.dnLbl} required>
          <input data-testid="rp-dn" type="number" step="0.1" min="1" value={diameter} onChange={(e) => setDiameter(e.target.value)} required
            className="wb-input w-full px-3 py-2 text-sm font-mono" />
        </Field>
        <Field label={t.realprog.wallLbl} required>
          <input data-testid="rp-wall" type="number" step="0.05" min="0.1" value={wall} onChange={(e) => setWall(e.target.value)} required
            className="wb-input w-full px-3 py-2 text-sm font-mono" />
        </Field>
      </div>

      <div className="mt-3">
        <Field label={t.realprog.paramsLbl} required>
          <textarea data-testid="rp-params" value={parameters} onChange={(e) => setParameters(e.target.value)} required
            placeholder={t.realprog.paramsPh} rows={6}
            className="wb-input w-full px-3 py-2 text-xs font-mono resize-y" />
        </Field>
      </div>

      <div className="mt-3">
        <Field label={t.realprog.notesLbl}>
          <textarea data-testid="rp-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={t.realprog.notesPh} rows={2}
            className="wb-input w-full px-3 py-2 text-xs resize-y" />
        </Field>
      </div>

      {err && (
        <div className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <div className="mt-4 flex gap-2 justify-end">
        <button type="button" onClick={onClose}
          className="wb-btn-ghost px-4 py-2 text-sm">{t.realprog.cancel}</button>
        <button type="submit" disabled={saving} data-testid="rp-submit"
          className="wb-btn-primary px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.realprog.saving}</> : <>{t.realprog.submit}</>}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
        {label} {required && <span className="text-gold-500">*</span>}
      </span>
      {children}
    </label>
  );
}
